// lib/world/terrainHeight.js
//
// Builds a heightmap grid from the terrain floor BufferGeometry so the viewer
// can snap the bird's feet to the ground surface each frame.
//
// Why a heightmap and not a raycast: the app mixes `three/webgpu` (terrain is
// loaded/rendered under the R3F/webgpu reconciler) and plain `three`
// (ServerNPCBridge). `Raycaster.intersectObject` does internal instanceof/type
// checks and silently returns 0 hits when the geometry was created by a
// different `three` build. This module reads the geometry's raw typed arrays
// (position + index) — zero class coupling, immune to that hazard — and the
// per-frame lookup is O(1).
//
// The floor geometry is static, so the field is built once on load.

/**
 * Build a sampleable height field from a floor BufferGeometry.
 *
 * @param {object} geometry - a THREE BufferGeometry with a `position` attribute
 *   (and ideally an index). Local coords are treated as world coords — the
 *   floor node renders at identity (see TestMap). If a future terrain bakes a
 *   node transform, positions must be pre-multiplied by matrixWorld before here.
 * @param {object} [opts]
 * @param {number} [opts.resolution=256] - grid cells per side (~1 unit/cell on a
 *   250-unit floor). Bump to 512 if steep faces look terraced.
 * @param {number} [opts.pad=0.5] - world padding around the bbox so edge samples
 *   never fall outside the grid.
 * @returns {null | {
 *   sample: (x:number, z:number) => number,
 *   minX:number, maxX:number, minZ:number, maxZ:number,
 *   minY:number, maxY:number, cols:number, rows:number,
 *   filledCells:number,
 * }}
 */
export function buildHeightField(geometry, { resolution = 256, pad = 0.5 } = {}) {
  if (!geometry || !geometry.attributes || !geometry.attributes.position) return null

  if (!geometry.boundingBox) geometry.computeBoundingBox()
  const bb = geometry.boundingBox
  const minX = bb.min.x - pad
  const maxX = bb.max.x + pad
  const minZ = bb.min.z - pad
  const maxZ = bb.max.z + pad

  const cols = Math.max(2, resolution | 0)
  const rows = cols
  const spanX = maxX - minX || 1
  const spanZ = maxZ - minZ || 1
  const dx = spanX / (cols - 1)
  const dz = spanZ / (rows - 1)

  const height = new Float32Array(cols * rows)
  height.fill(NaN)

  const pos = geometry.attributes.position.array
  const idx = geometry.index ? geometry.index.array : null
  const triCount = idx ? (idx.length / 3) | 0 : (pos.length / 9) | 0

  // Rasterize every triangle into the grid cells its XZ projection covers,
  // writing barycentric-interpolated Y. Keep the topmost surface per cell so
  // overhangs/undersides never win over the walkable top.
  for (let t = 0; t < triCount; t++) {
    const ia = (idx ? idx[t * 3] : t * 3) * 3
    const ib = (idx ? idx[t * 3 + 1] : t * 3 + 1) * 3
    const ic = (idx ? idx[t * 3 + 2] : t * 3 + 2) * 3

    const ax = pos[ia], ay = pos[ia + 1], az = pos[ia + 2]
    const bx = pos[ib], by = pos[ib + 1], bz = pos[ib + 2]
    const cx = pos[ic], cy = pos[ic + 1], cz = pos[ic + 2]

    // Barycentric denominator in the XZ plane.
    const d = (bz - cz) * (ax - cx) + (cx - bx) * (az - cz)
    if (d === 0) continue // degenerate when projected to XZ
    const invD = 1 / d

    // Triangle XZ bbox → clamped cell range.
    const tMinX = Math.min(ax, bx, cx)
    const tMaxX = Math.max(ax, bx, cx)
    const tMinZ = Math.min(az, bz, cz)
    const tMaxZ = Math.max(az, bz, cz)

    let i0 = Math.floor((tMinX - minX) / dx)
    let i1 = Math.ceil((tMaxX - minX) / dx)
    let j0 = Math.floor((tMinZ - minZ) / dz)
    let j1 = Math.ceil((tMaxZ - minZ) / dz)
    if (i0 < 0) i0 = 0
    if (j0 < 0) j0 = 0
    if (i1 > cols - 1) i1 = cols - 1
    if (j1 > rows - 1) j1 = rows - 1

    for (let j = j0; j <= j1; j++) {
      const wz = minZ + j * dz
      for (let i = i0; i <= i1; i++) {
        const wx = minX + i * dx
        const u = ((bz - cz) * (wx - cx) + (cx - bx) * (wz - cz)) * invD
        const v = ((cz - az) * (wx - cx) + (ax - cx) * (wz - cz)) * invD
        const w = 1 - u - v
        const eps = -1e-4
        if (u < eps || v < eps || w < eps) continue // point outside the triangle
        const y = u * ay + v * by + w * cy
        const k = j * cols + i
        const cur = height[k]
        if (Number.isNaN(cur) || y > cur) height[k] = y
      }
    }
  }

  const filledCells = fillHoles(height, cols, rows)

  /**
   * Bilinear surface height at world (x, z). Clamps to the grid; NaN-guards each
   * corner so a padded-border sample never returns NaN.
   */
  function sample(x, z) {
    let fx = (x - minX) / dx
    let fz = (z - minZ) / dz
    if (fx < 0) fx = 0; else if (fx > cols - 1) fx = cols - 1
    if (fz < 0) fz = 0; else if (fz > rows - 1) fz = rows - 1
    const i0 = Math.floor(fx)
    const j0 = Math.floor(fz)
    const i1 = i0 + 1 > cols - 1 ? cols - 1 : i0 + 1
    const j1 = j0 + 1 > rows - 1 ? rows - 1 : j0 + 1
    const tx = fx - i0
    const tz = fz - j0

    let h00 = height[j0 * cols + i0]
    let h10 = height[j0 * cols + i1]
    let h01 = height[j1 * cols + i0]
    let h11 = height[j1 * cols + i1]
    const fallback = nanPick(h00, h10, h01, h11)
    if (Number.isNaN(h00)) h00 = fallback
    if (Number.isNaN(h10)) h10 = fallback
    if (Number.isNaN(h01)) h01 = fallback
    if (Number.isNaN(h11)) h11 = fallback

    const top = h00 + (h10 - h00) * tx
    const bot = h01 + (h11 - h01) * tx
    return top + (bot - top) * tz
  }

  return {
    sample,
    minX, maxX, minZ, maxZ,
    minY: bb.min.y, maxY: bb.max.y,
    cols, rows, filledCells,
  }
}

function nanPick(a, b, c, d) {
  if (!Number.isNaN(a)) return a
  if (!Number.isNaN(b)) return b
  if (!Number.isNaN(c)) return c
  if (!Number.isNaN(d)) return d
  return 0
}

/**
 * Fill NaN cells (gaps between rasterized triangles, padded border) from valid
 * 4-neighbours over a few bounded passes, then zero any survivors so `sample`
 * never returns NaN. Returns the count of cells that ended up with a real value.
 */
function fillHoles(height, cols, rows) {
  const maxPasses = 8
  for (let pass = 0; pass < maxPasses; pass++) {
    let filled = 0
    let remaining = 0
    for (let j = 0; j < rows; j++) {
      for (let i = 0; i < cols; i++) {
        const k = j * cols + i
        if (!Number.isNaN(height[k])) continue
        let sum = 0
        let n = 0
        if (i > 0 && !Number.isNaN(height[k - 1])) { sum += height[k - 1]; n++ }
        if (i < cols - 1 && !Number.isNaN(height[k + 1])) { sum += height[k + 1]; n++ }
        if (j > 0 && !Number.isNaN(height[k - cols])) { sum += height[k - cols]; n++ }
        if (j < rows - 1 && !Number.isNaN(height[k + cols])) { sum += height[k + cols]; n++ }
        if (n > 0) { height[k] = sum / n; filled++ }
        else remaining++
      }
    }
    if (remaining === 0 || filled === 0) break
  }
  let valid = 0
  for (let k = 0; k < height.length; k++) {
    if (Number.isNaN(height[k])) height[k] = 0
    else valid++
  }
  return valid
}
